import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import toast from 'react-hot-toast';
import { ImagePlus, Loader2, Mic, RotateCw, Send, Trash2, X } from 'lucide-react';
import clsx from 'clsx';

import { ReplyBar } from '@/components/chat/ReplyQuote';
import { formatClock } from '@/components/chat/VoicePlayer';
import { apiErrorMessage, apiPost, apiUpload } from '@/lib/api';
import type { ChatMessage, ChatReply } from '@/lib/chat';
import { CHAT_PHOTO, formatBytes, prepareImage } from '@/lib/image-compress';
import { VOICE_MAX_MS, VOICE_MIN_MS, VoiceRecorder, voiceFileName, voiceSupported } from '@/lib/voice-recorder';

interface AttachedPhoto {
  blob: Blob;
  name: string;
  previewUrl: string;
  originalSize: number;
}

const LIVE_BARS = 28;

/** Живая волна во время записи: свежие столбики появляются справа. */
function LiveBars({ levels }: { levels: number[] }) {
  const padded = [...Array<number>(Math.max(0, LIVE_BARS - levels.length)).fill(0), ...levels.slice(-LIVE_BARS)];
  return (
    <div className="flex h-6 min-w-0 flex-1 items-center justify-end gap-[2px] overflow-hidden" aria-hidden>
      {padded.map((l, i) => (
        <span
          key={i}
          className="w-[3px] shrink-0 rounded-full bg-orange/70"
          style={{ height: `${Math.max(12, Math.round(l * 100))}%` }}
        />
      ))}
    </div>
  );
}

function micError(err: unknown): string {
  const name = (err as { name?: string } | null)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Нет доступа к микрофону — разрешите его в настройках браузера';
  }
  if (name === 'NotFoundError') return 'Микрофон не найден';
  return 'Не удалось включить микрофон';
}

export default function Composer({
  inputRef,
  replyTo,
  onCancelReply,
  onSent,
}: {
  inputRef: RefObject<HTMLTextAreaElement | null>;
  /** На что отвечаем (свайп или «Ответить»), иначе null. */
  replyTo: ChatReply | null;
  onCancelReply: () => void;
  /** replyId — на что отвечало ушедшее сообщение: полосу ответа снимает родитель. */
  onSent: (m: ChatMessage, replyId: string | null) => void;
}) {
  // Голосовое досылается таймером, начатым в прошлом рендере, — берём актуальный ответ.
  const replyRef = useRef(replyTo);
  useLayoutEffect(() => {
    replyRef.current = replyTo;
  });
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState<AttachedPhoto | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [levels, setLevels] = useState<number[]>([]);
  // Голосовое, которое не ушло (связь) — не теряем, даём повторить.
  const [failedVoice, setFailedVoice] = useState<FormData | null>(null);
  const recorderRef = useRef<VoiceRecorder | null>(null);
  const tickRef = useRef(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const canVoice = useMemo(() => voiceSupported(), []);

  useEffect(
    () => () => {
      window.clearInterval(tickRef.current);
      recorderRef.current?.cancel();
    },
    [],
  );

  useEffect(
    () => () => {
      if (photo) URL.revokeObjectURL(photo.previewUrl);
    },
    [photo],
  );

  // Высота поля — по тексту, до пяти строк.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [text, inputRef, recording]);

  /** Ответ кладём в форму первым полем: так он доедет и при «Повторить» у голосового. */
  const newForm = (): FormData => {
    const form = new FormData();
    const replyId = replyRef.current?.id;
    if (replyId) form.append('replyToId', replyId);
    return form;
  };

  const upload = async (form: FormData, failText: string): Promise<boolean> => {
    setSending(true);
    setProgress(0);
    try {
      const msg = await apiUpload<ChatMessage>('/chat/media', form, { silent: true, onProgress: setProgress });
      const replyId = form.get('replyToId');
      onSent(msg, typeof replyId === 'string' ? replyId : null);
      return true;
    } catch (err) {
      toast.error(apiErrorMessage(err, failText));
      return false;
    } finally {
      setSending(false);
      setProgress(null);
    }
  };

  const attach = async (file: File) => {
    if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
      toast.error('Можно отправить только фото');
      return;
    }
    setPreparing(true);
    try {
      const img = await prepareImage(file, CHAT_PHOTO);
      setPhoto({ blob: img.blob, name: img.name, previewUrl: URL.createObjectURL(img.blob), originalSize: file.size });
    } catch {
      toast.error('Не удалось открыть фото — попробуйте другое');
    } finally {
      setPreparing(false);
    }
  };

  const sendText = async () => {
    const value = text.trim();
    if (!value) return;
    const replyId = replyRef.current?.id ?? null;
    // Поле чистим сразу, как в мессенджере: сообщение по сокету приходит раньше ответа.
    setText('');
    setSending(true);
    // Клавиатуру не прячем — можно писать дальше.
    requestAnimationFrame(() => inputRef.current?.focus());
    try {
      const msg = await apiPost<ChatMessage>(
        '/chat',
        replyId ? { text: value, replyToId: replyId } : { text: value },
        { silent: true },
      );
      onSent(msg, replyId);
    } catch (err) {
      // Не ушло — возвращаем текст, если человек не начал новое.
      setText((cur) => (cur ? cur : value));
      toast.error(apiErrorMessage(err, 'Сообщение не отправлено'));
    } finally {
      setSending(false);
    }
  };

  const sendPhoto = async (p: AttachedPhoto) => {
    const form = newForm();
    form.append('kind', 'photo');
    form.append('text', text.trim());
    form.append('file', p.blob, p.name);
    if (await upload(form, 'Фото не отправлено')) {
      setPhoto(null);
      setText('');
    }
  };

  const send = () => {
    if (sending || preparing) return;
    if (photo) void sendPhoto(photo);
    else void sendText();
  };

  const stopTicker = () => {
    window.clearInterval(tickRef.current);
    tickRef.current = 0;
  };

  const cancelRecording = () => {
    stopTicker();
    recorderRef.current?.cancel();
    recorderRef.current = null;
    setRecording(false);
    setLevels([]);
  };

  const sendVoice = async (form: FormData) => {
    setFailedVoice(null);
    if (!(await upload(form, 'Голосовое не отправлено'))) setFailedVoice(form);
  };

  const finishRecording = async () => {
    const r = recorderRef.current;
    if (!r) return;
    recorderRef.current = null;
    stopTicker();
    setRecording(false);
    setLevels([]);
    let rec;
    try {
      rec = await r.stop();
    } catch {
      toast.error('Запись не получилась');
      return;
    }
    if (rec.durationMs < VOICE_MIN_MS || rec.blob.size < 512) {
      toast('Слишком короткое голосовое');
      return;
    }
    const form = newForm();
    form.append('kind', 'voice');
    form.append('durationMs', String(rec.durationMs));
    form.append('waveform', JSON.stringify(rec.waveform));
    form.append('file', rec.blob, voiceFileName(rec.mime));
    await sendVoice(form);
  };

  const startRecording = async () => {
    if (recording || sending || recorderRef.current) return;
    const r = new VoiceRecorder();
    recorderRef.current = r;
    r.onLevel = (l) => setLevels((cur) => [...cur.slice(-(LIVE_BARS - 1)), l]);
    try {
      await r.start();
    } catch (err) {
      recorderRef.current = null;
      toast.error(micError(err));
      return;
    }
    inputRef.current?.blur();
    setElapsed(0);
    setRecording(true);
    tickRef.current = window.setInterval(() => {
      setElapsed(r.elapsedMs);
      if (r.elapsedMs >= VOICE_MAX_MS) void finishRecording();
    }, 200);
  };

  const showSend = text.trim().length > 0 || !!photo || !canVoice;

  return (
    <div className="relative shrink-0 border-t border-line/70 bg-white">
      {progress != null ? (
        <div className="absolute inset-x-0 top-0 h-0.5 bg-orange/15" aria-hidden>
          <div className="h-full bg-orange transition-[width] duration-200" style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
      ) : null}
      <div className="mx-auto max-w-3xl px-2 py-2 sm:px-4">
        {replyTo ? <ReplyBar reply={replyTo} onCancel={onCancelReply} /> : null}

        {photo ? (
          <div className="mb-2 flex items-center gap-3 rounded-xl border border-line bg-paper/70 p-2">
            <img src={photo.previewUrl} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-medium">Фото</div>
              <div className="truncate text-xs text-ink/50">
                {formatBytes(photo.blob.size)}
                {photo.blob.size < photo.originalSize * 0.9 ? ` · сжато из ${formatBytes(photo.originalSize)}` : ''}
                {sending && progress != null ? ` · ${Math.round(progress * 100)}%` : ''}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPhoto(null)}
              disabled={sending}
              className="rounded-full p-2 text-ink/45 hover:bg-ink/5 hover:text-ink disabled:opacity-40"
              aria-label="Убрать фото"
            >
              <X size={18} aria-hidden />
            </button>
          </div>
        ) : null}

        {failedVoice && !recording ? (
          <div className="mb-2 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            <span className="min-w-0 flex-1">Голосовое не отправилось</span>
            <button
              type="button"
              onClick={() => void sendVoice(failedVoice)}
              disabled={sending}
              className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-rose-700 ring-1 ring-rose-200 disabled:opacity-50"
            >
              <RotateCw size={13} aria-hidden /> Повторить
            </button>
            <button
              type="button"
              onClick={() => setFailedVoice(null)}
              className="rounded-full p-1.5 text-rose-700/70 hover:bg-white"
              aria-label="Не отправлять"
            >
              <X size={16} aria-hidden />
            </button>
          </div>
        ) : null}

        {recording ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={cancelRecording}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink/55 hover:bg-ink/5 hover:text-rose-600"
              aria-label="Отменить запись"
            >
              <Trash2 size={20} aria-hidden />
            </button>
            <div className="flex h-11 min-w-0 flex-1 items-center gap-2.5 rounded-[22px] bg-paper px-4">
              <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-rose-500" aria-hidden />
              <span className="shrink-0 text-sm font-medium tabular-nums">{formatClock(elapsed)}</span>
              <LiveBars levels={levels} />
            </div>
            <button
              type="button"
              onClick={() => void finishRecording()}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-orange text-white active:scale-95"
              aria-label="Отправить голосовое"
            >
              <Send size={18} aria-hidden />
            </button>
          </div>
        ) : (
          <form
            className="flex items-end gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={sending || preparing}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink/55 hover:bg-ink/5 hover:text-ink disabled:opacity-40"
              aria-label="Прикрепить фото"
            >
              {preparing ? <Loader2 size={20} className="animate-spin" aria-hidden /> : <ImagePlus size={21} aria-hidden />}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) void attach(f);
              }}
            />
            <textarea
              ref={inputRef}
              rows={1}
              className="max-h-[140px] min-h-[44px] min-w-0 flex-1 resize-none rounded-[22px] border border-line bg-white px-4 py-2.5 leading-snug focus:border-orange/60 focus:outline-none focus:ring-2 focus:ring-orange/20"
              placeholder={photo ? 'Подпись к фото' : 'Сообщение'}
              maxLength={2000}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && replyTo) {
                  e.preventDefault();
                  onCancelReply();
                  return;
                }
                // На телефоне Enter — перенос строки, отправка кнопкой.
                if (
                  e.key === 'Enter' &&
                  !e.shiftKey &&
                  !e.nativeEvent.isComposing &&
                  window.matchMedia('(pointer: fine)').matches
                ) {
                  e.preventDefault();
                  send();
                }
              }}
              onPaste={(e) => {
                const f = Array.from(e.clipboardData.files).find((x) => x.type.startsWith('image/'));
                if (f) {
                  e.preventDefault();
                  void attach(f);
                }
              }}
              aria-label="Сообщение"
            />
            {showSend ? (
              <button
                type="submit"
                className={clsx(
                  'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-orange text-white transition active:scale-95 disabled:opacity-50',
                )}
                disabled={sending || preparing || (!text.trim() && !photo)}
                aria-label="Отправить"
              >
                {sending ? <Loader2 size={18} className="animate-spin" aria-hidden /> : <Send size={18} aria-hidden />}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void startRecording()}
                disabled={sending}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-orange text-white transition active:scale-95 disabled:opacity-50"
                aria-label="Записать голосовое"
              >
                {sending ? <Loader2 size={18} className="animate-spin" aria-hidden /> : <Mic size={20} aria-hidden />}
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
